import React, { useCallback, useEffect, useState } from "react";
import MainLayout from "../components/layout/MainLayout";
import { Card } from "../components/ui/card";
import { format, parseISO } from "date-fns";
import { dashboardApi } from "../services/dashboardApi";
import { ComebacksChart, EmailDetails, GEO_LABELS, num, pct } from "../components/SimplyloanEmailDetails";
import { SimplyloanSmsContent } from "./SimplyloanSmsPage";

const FLAGS: Record<string, string> = { se: "se", no: "no", fi: "fi" };

// Status always shows icon + label, never color alone
const MARKET_STATUS: Record<string, { cls: string; label: string; text?: string }> = {
    ok: { cls: "bg-green-100 text-green-800", label: "● Healthy" },
    watch: { cls: "bg-yellow-100 text-yellow-800", label: "⚠ Watching" },
    gmail_flag: {
        cls: "bg-orange-100 text-orange-800",
        label: "⚠ Gmail flag",
        text: "Gmail flagged too many spam reports. People who haven't clicked get every other email, and no dormant contacts are added until it clears.",
    },
    problem: { cls: "bg-red-100 text-red-800", label: "✗ Problem" },
    shadow: { cls: "bg-gray-100 text-gray-600", label: "○ Reactivation off" },
};

// Above Gmail's 0.1% guideline: worth a look
const COMPLAINT_WARN = 0.001;

function sendTime(d: string | null): string {
    if (!d) return "—";
    try { return format(parseISO(d), "EEE d MMM, HH:mm"); } catch { return d; }
}

function Flag({ geo }: { geo: string }) {
    return <img src={`https://flagcdn.com/w20/${FLAGS[geo]}.png`} alt="" className="h-3 w-[18px] inline-block rounded-sm" />;
}

function MarketCard({ geo, m }: { geo: string; m: any }) {
    const st = MARKET_STATUS[m.status] || MARKET_STATUS.ok;
    const note = st.text || (m.status !== "ok" ? m.reason : null);
    return (
        <Card className="p-4 shadow-sm">
            <div className="flex items-center justify-between gap-2">
                <h2 className="font-semibold flex items-center gap-2"><Flag geo={geo} /> {GEO_LABELS[geo]}</h2>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.label}</span>
            </div>
            <p className="mt-3 text-2xl font-bold">{m.audience != null ? num(m.audience) : "—"}</p>
            <p className="text-xs text-gray-500">
                emails today{m.dormant ? `, incl. ${num(m.dormant)} dormant` : ""}
            </p>
            <dl className="mt-3 space-y-1 text-sm">
                <div className="flex justify-between"><dt className="text-gray-500">Can be emailed</dt><dd>{num(m.mailable)}</dd></div>
                <div className="flex justify-between">
                    <dt className="text-gray-500">Dormant tried / came back</dt>
                    <dd>{num(m.tried)} / {num(m.came_back)}</dd>
                </div>
                <div className="flex justify-between"><dt className="text-gray-500">Dormant still waiting</dt><dd>{num(m.waiting)}</dd></div>
            </dl>
            {note && <p className="mt-3 text-xs text-gray-600 border-t pt-2">{note}</p>}
        </Card>
    );
}

function RecentSends({ rows }: { rows: any[] }) {
    if (rows.length === 0) return <p className="text-sm text-gray-500 p-4">No sends yet.</p>;
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b text-xs text-gray-500">
                        <th className="py-2 px-4 text-left font-medium">Sent</th>
                        <th className="py-2 px-3 text-left font-medium">Advertiser</th>
                        <th className="py-2 px-3 text-right font-medium">Delivered</th>
                        <th className="py-2 px-3 text-right font-medium">Clicks</th>
                        <th className="py-2 px-3 text-right font-medium">Unsubs</th>
                        <th className="py-2 px-3 text-right font-medium">Complaints</th>
                        <th className="py-2 px-4 text-right font-medium">Bounces</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => {
                        const highComplaints = r.sent > 0 && r.complaints / r.sent > COMPLAINT_WARN;
                        return (
                            <tr key={r.campaign_id} className="border-b border-gray-50 hover:bg-gray-50/60">
                                <td className="py-2 px-4 whitespace-nowrap" title={r.name}>
                                    <Flag geo={r.geo} /> <span className="ml-1">{sendTime(r.sent_date)}</span>
                                </td>
                                <td className="py-2 px-3">{r.advertiser || "—"}</td>
                                <td className="py-2 px-3 text-right">{num(r.sent)}</td>
                                <td className="py-2 px-3 text-right">
                                    {num(r.clicks)} <span className="text-xs text-gray-400">{pct(r.clicks, r.sent, 2)}</span>
                                </td>
                                <td className="py-2 px-3 text-right">{num(r.unsubs)}</td>
                                <td className={`py-2 px-3 text-right ${highComplaints ? "text-red-700 font-semibold" : ""}`}>
                                    {highComplaints && "⚠ "}{num(r.complaints)}
                                    {r.complaints > 0 && <span className="text-xs font-normal text-gray-400"> {pct(r.complaints, r.sent, 2)}</span>}
                                </td>
                                <td className="py-2 px-4 text-right">{num(r.bounces)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

const SimplyloanEmailPage: React.FC = () => {
    const [channelTab, setChannelTab] = useState<"email" | "sms">("email");
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setData(await dashboardApi.getSimplyloanOverview());
        } catch (e: any) {
            setError(e?.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const geos: string[] = data?.geos || ["se", "no", "fi"];
    const mode = data?.phase2?.mode;

    return (
        <MainLayout>
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-6">
                {/* Channel Switcher Tab Bar */}
                <div className="flex items-center gap-6 border-b border-gray-200">
                    <button
                        onClick={() => setChannelTab("email")}
                        className={`py-2.5 text-sm border-b-2 -mb-px transition-colors ${
                            channelTab === "email"
                                ? "border-gray-900 font-semibold text-gray-900"
                                : "border-transparent text-gray-500 hover:text-gray-800"
                        }`}
                    >
                        Email
                    </button>
                    <button
                        onClick={() => setChannelTab("sms")}
                        className={`py-2.5 text-sm border-b-2 -mb-px transition-colors ${
                            channelTab === "sms"
                                ? "border-gray-900 font-semibold text-gray-900"
                                : "border-transparent text-gray-500 hover:text-gray-800"
                        }`}
                    >
                        SMS
                    </button>
                </div>

                {channelTab === "sms" ? (
                    <SimplyloanSmsContent />
                ) : (
                    <>
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h1 className="text-2xl font-bold">SimplyLoan email</h1>
                                <p className="text-sm text-gray-500 mt-1">
                                    {data?.snapshot_date ? `Audience updated ${data.snapshot_date}. ` : ""}
                                    Runs by itself every morning at 06:00.
                                    {mode === "paused" && " Reactivation is paused: no dormant contacts are added."}
                                </p>
                            </div>
                            <button
                                onClick={load}
                                disabled={loading}
                                className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-md text-gray-600 disabled:opacity-50"
                            >
                                {loading ? "Loading…" : "↻ Refresh"}
                            </button>
                        </div>

                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
                                Couldn&apos;t load: {error}
                            </div>
                        )}

                        {loading && !data ? (
                            <div className="space-y-6 animate-pulse">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {[1, 2, 3].map((i) => <div key={i} className="h-48 bg-gray-100 rounded-xl" />)}
                                </div>
                                <div className="h-96 bg-gray-100 rounded-xl" />
                            </div>
                        ) : data && (
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {geos.map((g) => data.markets?.[g] && <MarketCard key={g} geo={g} m={data.markets[g]} />)}
                                </div>

                                <Card className="shadow-sm">
                                    <h2 className="font-semibold px-4 pt-4 pb-2">Last sends</h2>
                                    <RecentSends rows={data.recent_sends || []} />
                                </Card>

                                <Card className="shadow-sm p-4">
                                    <h2 className="font-semibold">Dormant contacts who came back</h2>
                                    <p className="text-xs text-gray-500 mb-3">
                                        A click or real open after 90+ quiet days, per week. Orange means a reactivation email went out in the 14 days before.
                                    </p>
                                    <ComebacksChart rows={data.reengaged_weekly || []} />
                                </Card>

                                <details className="group">
                                    <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-900 select-none">
                                        <span className="group-open:hidden">▸ Show details</span>
                                        <span className="hidden group-open:inline">▾ Hide details</span>
                                    </summary>
                                    <Card className="shadow-sm p-4 mt-3">
                                        <EmailDetails data={data} />
                                    </Card>
                                </details>
                            </>
                        )}
                    </>
                )}
            </div>
        </MainLayout>
    );
};

export default SimplyloanEmailPage;
